// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { ProductType } from '@shared/utils/Instruments/ProductType';
import { CurPriceSource } from '@shared/utils/Instruments/CurPriceSource';
import { SlTpPriceType } from '@shared/utils/Enums/Constants';
import { NO_BIND_TO } from '@shared/utils/Trading/OrderConstants';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { QuoteValid } from '@shared/utils/Quotes/QuoteValid';
import { OrderUtils } from '@shared/utils/Trading/OrderUtils';
import { DataCache } from '../DataCache';
import { SlTpUtils } from '@shared/utils/Trading/SlTpUtils';
import { SlTpHolder } from '@shared/utils/Trading/SlTpHolder';
import { ResourcesManager } from '../properties/ResourcesManager';
export class Order {
    constructor(dataCache, quoteController, paramObj) {
        this._prT = ProductType.General;
        this.StopLimit = NaN;
        this.BoundTo = NO_BIND_TO;
        this.RealActive = false; // TODO. Huh? Quote: "Из за неправильно логики с Active !!!".
        this.ExpireAt = null;
        this.SharesFilled = 0;
        this.AverageFilledPrice = 0;
        // Originally, type used to be long.
        this.OrderGroupId = null;
        this.PositionId = null;
        this.SuperPositionId = null; // (FIFO) #80010
        this.Exchange = null;
        this.TrStopOffset = 0;
        this.Status = -1;
        this.FStrikePrice = 0;
        this.StopLossPriceValue = NaN;
        this.TakeProfitPriceValue = NaN;
        this.StopLossLimitPriceValue = null; // #91413
        this.TakeProfitPriceType = SlTpPriceType.Absolute;
        this.StopLossPriceType = SlTpPriceType.Absolute;
        this.TriggerSL = null; // #109798
        this.TriggerTP = null;
        this.TriggerSLTP = null;
        this.Replaceable = false;
        this.isActivated = false; // #92963
        this.CurPriceOpen = NaN;
        this.CurPriceClose = NaN;
        this._curPriceCloseNotRounded = NaN; // #116267
        this.LastQuote = null;
        this.OnUpdate = new CustomEvent();
        this.OnPriceChanged = new CustomEvent();
        this.isPosition = false;
        this.lastPriceUpdated = false;
        this.Comment = null;
        this.UserComment = null;
        this.StrategyId = null;
        this.ExecutionType = null;
        this.IsAllowOTHTrading = null;
        this.DataCache = dataCache;
        this.QuoteController = quoteController;
        this.OrderNumber = paramObj.OrderNumber;
        this.Route = paramObj.Route;
        this.Instrument = paramObj.Instrument;
        this.Account = paramObj.Account;
        this.OrderType = paramObj.OrderType;
        this.Amount = paramObj.Amount;
        this.DisclosedQty = paramObj.DisclosedQty;
        this.Price = paramObj.Price;
        this.BuySell = paramObj.BuySell;
        this.TimeInForce = paramObj.TimeInForce;
        this.UTCDateTime = paramObj.UTCDateTime;
        this.ProductType = paramObj.ProductType;
        // TODO. Move field to Position.
        this.ServerCalculation = paramObj.ServerCalculation;
        this.Active = paramObj.Active;
        this.Leverage = paramObj.Leverage || null;
        this.SubscribeToQuotes();
        // TODO. Remove. Ugly.
        Order.TrySubscribeLocalizeStatic();
    }
    get IsActivated() {
        return this.isActivated;
    }
    set IsActivated(value) {
        this.isActivated = value;
    }
    get isActive() {
        return false;
    }
    isClosingOrder() {
        return this.PositionId && this.PositionId !== '-1';
    }
    SubscribeToQuotes() {
        this.QuoteController.addListener(this.Instrument, this, HistoryType.QUOTE_LEVEL1);
        if (this.AllowSubscribeOnTrades()) {
            this.QuoteController.addListener(this.Instrument, this, HistoryType.QUOTE_TRADES);
        }
        this.Instrument.NewQuote.Subscribe(this.newQuote, this);
    }
    UnsubscribeFromQuotes() {
        this.QuoteController.removeListener(this.Instrument, this, HistoryType.QUOTE_LEVEL1);
        if (this.AllowSubscribeOnTrades()) {
            this.QuoteController.removeListener(this.Instrument, this, HistoryType.QUOTE_TRADES);
        }
        this.Instrument.NewQuote.UnSubscribe(this.newQuote, this);
    }
    AllowSubscribeOnTrades() {
        if (this.Instrument == null) {
            return false;
        }
        return (this.Instrument.CurPriceSource == CurPriceSource.Trades ||
            this.Instrument.CurPriceSource == CurPriceSource.Level1MainOnly);
    }
    Dispose() {
        this.UnsubscribeFromQuotes();
    }
    newQuote(newQuoteMsg) {
        let quote1Msg = null;
        if (newQuoteMsg.Type === HistoryType.QUOTE_LEVEL1) {
            quote1Msg = newQuoteMsg;
        }
        else if (
        // #43020 если пришла невалидная котировка Quote1, то за ней может прийти IDPM или Quote3 и обновить last, поэтому мы должны в этом случае сделать перерасчет CurPriceOpen и CurPriceClose
        newQuoteMsg.Type === HistoryType.QUOTE_INSTRUMENT_DAY_BAR ||
            newQuoteMsg.Type === HistoryType.QUOTE_TRADES) {
            quote1Msg = this.Instrument.GetLastQuote(QuoteValid.Last);
        }
        if (!quote1Msg) {
            return;
        }
        const lastPrice = this.Instrument.Level1.GetLastPrice(this.Account);
        // TODO negativ
        // if (lastPrice < 0) lastPrice = 0;
        const sp = this.DataCache.GetSpreadPlan(this.Account);
        const bid = !isNaN(quote1Msg.Bid) ? quote1Msg.BidSpread_SP_Ins(sp, this.Instrument) : lastPrice;
        const ask = !isNaN(quote1Msg.Ask) ? quote1Msg.AskSpread_SP_Ins(sp, this.Instrument) : lastPrice;
        this.CurPriceOpen = this.BuySell === OperationType.Buy ? ask : bid;
        if (isNaN(this.CurPriceOpen)) {
            this.CurPriceOpen = lastPrice;
        }
        if (!this.Instrument.CalcPriceOnTrade()) {
            this.CurPriceClose = this.CurPriceCloseNotRounded = this.BuySell === OperationType.Buy ? bid : ask;
            this.lastPriceUpdated = true;
            if (isNaN(this.CurPriceClose)) {
                this.CurPriceClose = this.CurPriceCloseNotRounded = lastPrice;
            }
        }
        else if (!isNaN(lastPrice)) {
            this.CurPriceClose = this.CurPriceCloseNotRounded = lastPrice;
            this.lastPriceUpdated = true;
        }
        this.LastQuote = quote1Msg;
        // TODO.
        // Самостоятельно следим за ценами TS
        // (пока актуально для оанды, планируем и на наш сервер перенести)
        if (this.DataCache.ManualControlPriceTStopOrders &&
            this.OrderType === OrderType.TrailingStop &&
            this.RealActive &&
            (this.BoundTo !== NO_BIND_TO || this.PositionId !== NO_BIND_TO)) {
            let newPrice = NaN;
            let setNewPrice = false;
            if (this.BuySell === OperationType.Buy) {
                newPrice = this.CurPriceOpen + this.TrStopOffset;
                setNewPrice = newPrice < this.Price || (!this.Price && !!newPrice);
            }
            else {
                newPrice = this.CurPriceOpen - this.TrStopOffset;
                setNewPrice = newPrice > this.Price || (!this.Price && !!newPrice);
            }
            if (setNewPrice) {
                this.Price = newPrice;
                this.OnPriceChanged.Raise();
            }
        }
        this.OnUpdate.Raise(this);
    }
    GetFullOperationName(isPositionShortLongWords) {
        isPositionShortLongWords = isPositionShortLongWords || false;
        const operationType = OperationType;
        let operationName = null;
        switch (this.BuySell) {
            case operationType.Buy:
                operationName = isPositionShortLongWords ? Order.generaltradingShort : Order.generaltradingBuy;
                break;
            case operationType.Sell:
                operationName = isPositionShortLongWords ? Order.generaltradingLong : Order.generaltradingSell;
                break;
        }
        return operationName;
    }
    getInitMargin(useAccountCurr) {
        // if (useAccountCurr || Account == null)
        //    return initMargin;
        // else
        //    return initMargin / Account.getCrossPrice();
        return 1;
    }
    GetBasePriceForTrailingStop() {
        return this.Price;
    }
    GetStopLossInPriceValue() {
        const sltpHolder = this.createSlTpHolder();
        if (!sltpHolder.isSlEmpty()) {
            return SlTpUtils.getSLTPPrice(this.Instrument, this.BuySell === OperationType.Buy, true, this.Price, this.StopLossPriceValue, this.StopLossPriceType);
        }
        else {
            return NaN;
        }
    }
    GetStopLossLimitInPriceValue() {
        const sltpHolder = this.createSlTpHolder();
        if (!sltpHolder.isSllEmpty()) {
            return SlTpUtils.getSLLPrice(this.Instrument, this.BuySell === OperationType.Buy, this.Price, this.StopLossPriceValue, this.StopLossLimitPriceValue, this.StopLossPriceType);
        }
        else {
            return NaN;
        }
    }
    GetTakeProfitInPriceValue() {
        const sltpHolder = this.createSlTpHolder();
        if (!sltpHolder.isTpEmpty()) {
            return SlTpUtils.getSLTPPrice(this.Instrument, this.BuySell === OperationType.Buy, false, this.Price, this.TakeProfitPriceValue, this.TakeProfitPriceType);
        }
        else {
            return NaN;
        }
    }
    GetStopLossInOffsetValue() {
        const sltpHolder = this.createSlTpHolder();
        if (!sltpHolder.isSlEmpty()) {
            return SlTpUtils.getSLTPOffset(this.Instrument, this.Price, this.StopLossPriceValue, this.StopLossPriceType);
        }
        else {
            return NaN;
        }
    }
    GetStopLossLimitInOffsetValue() {
        const sltpHolder = this.createSlTpHolder();
        if (!sltpHolder.isSllEmpty()) {
            return SlTpUtils.getSLLOffset(this.Instrument, this.StopLossPriceValue, this.StopLossLimitPriceValue, this.StopLossPriceType);
        }
        else {
            return NaN;
        }
    }
    GetTakeProfitInOffsetValue() {
        const sltpHolder = this.createSlTpHolder();
        if (!sltpHolder.isTpEmpty()) {
            return SlTpUtils.getSLTPOffset(this.Instrument, this.Price, this.TakeProfitPriceValue, this.TakeProfitPriceType);
        }
        else {
            return NaN;
        }
    }
    createSlTpHolder() {
        const sltpholder = new SlTpHolder();
        if (!isNullOrUndefined(this.StopLossPriceType)) {
            sltpholder.StopLossPriceType = this.StopLossPriceType;
        }
        sltpholder.StopLossPriceValue = this.StopLossPriceValue;
        sltpholder.StopLossLimitPriceValue = this.StopLossLimitPriceValue;
        if (!isNullOrUndefined(this.TakeProfitPriceType)) {
            sltpholder.TakeProfitPriceType = this.TakeProfitPriceType;
        }
        sltpholder.TakeProfitPriceValue = this.TakeProfitPriceValue;
        return sltpholder;
    }
    isOption() {
        return this.StrikePrice > 0;
    }
    getCrossPrice() {
        return this.DataCache.CrossRateCache.GetCrossPriceIns(this.Account, this.Instrument, this.BuySell);
    }
    // Если у позиции SL это StopLossLimit, то надо брать StopLimit иначе Price. Подробности -> https://tp.traderevolution.com/entity/91413
    getPriceForStopLoss() {
        return this.StopLimit && this.OrderType !== OrderType.TrailingStop ? this.StopLimit : this.Price;
    }
    getRealTrStopPrice() {
        if (!this.DataCache.isAllowedForMainAccount(RulesSet.FUNCTION_TRAILING_STOP_BY_PRICE)) {
            return null;
        }
        return this.Price;
        // похоже считать ненужно, он же есть у нас уже
        // let openPrice = this.getQuotePriceForTrStopCalculatingOffset()
        // let trStop = this.TrStopOffset
        // let sideSign = this.BuySell === OperationType.Buy ? 1 : -1;
        // //Посчитали истинные тики, соответсвенно потом в расчёте цены мы игнорим фракционы
        // // let ticks = Utils.toRawTicks(trStop, trStop.offsetType, this.Instrument) * sideSign;
        // let ticks = trStop * sideSign;
        // return this.Instrument.roundPrice(this.Instrument.CalculatePrice(openPrice, ticks, true))
    }
    getQuotePriceForTrStopCalculatingOffset() {
        const quote = this.LastQuote;
        if (!quote) {
            return 0;
        }
        const sp = this.DataCache.GetSpreadPlan(this.Account);
        return this.side === OperationType.Buy
            ? quote.AskSpread_SP_Ins(sp, this.Instrument)
            : quote.BidSpread_SP_Ins(sp, this.Instrument);
    }
    get AmountRemaining() {
        return this.Amount - this.SharesFilled;
    }
    get StrikePrice() {
        const ins = this.Instrument;
        return !this.FStrikePrice && ins && ins.StrikePrice > 0 ? ins.StrikePrice : this.FStrikePrice;
    }
    set StrikePrice(value) {
        this.FStrikePrice = value;
    }
    get CurPriceCloseNotRounded() {
        return !isNaN(this._curPriceCloseNotRounded) ? this._curPriceCloseNotRounded : this.CurPriceClose;
    }
    set CurPriceCloseNotRounded(value) {
        this._curPriceCloseNotRounded = value;
    }
    GetComment() {
        var _a;
        return (_a = (this.OrderType === OrderType.Care ? this.UserComment : this.Comment)) !== null && _a !== void 0 ? _a : '';
    }
    getParentOrder() {
        let parentOrder = DataCache.Trading.getPositionByBindTo(this.BoundTo);
        if (isNullOrUndefined(parentOrder)) {
            parentOrder = DataCache.Trading.getOrderById(this.BoundTo);
        }
        return parentOrder;
    }
    // TODO. Ugly. Remove.
    static TrySubscribeLocalizeStatic() {
        if (Order.localizeSubscribed) {
            return;
        }
        ResourcesManager.onLocaleChange.Subscribe(Order.Localize, Order);
        Order.localizeSubscribed = true;
        Order.Localize();
    }
    static Localize() {
        const operType = OperationType;
        Order.generaltradingBuy = Resources.getResource('general.trading.' + OrderUtils.getBuySellStr(operType.Buy));
        Order.generaltradingSell = Resources.getResource('general.trading.' + OrderUtils.getBuySellStr(operType.Sell));
        Order.generaltradingShort = Resources.getResource('general.trading.position.' + OrderUtils.getBuySellStr(operType.Buy));
        Order.generaltradingLong = Resources.getResource('general.trading.position.' + OrderUtils.getBuySellStr(operType.Sell));
    }
}
// // https://tp.traderevolution.com/entity/105525
// // id:106295 + id:106297 убрал
// Object.defineProperty(Order.prototype, 'ProductType', {
//     get: function ()
//     {
//         let val = this._prT
//         if (val === ProductType.CorporateAction)
//             val = ProductType.Delivery
//         return val;
//     },
//     set: function (value)
//     {
//         this._prT = value
//     }
// })
Order.generaltradingBuy = '';
Order.generaltradingSell = '';
Order.generaltradingShort = '';
Order.generaltradingLong = '';
